import React, { useState } from 'react';

import {
  useAccount,
  useSetUserRole,
  useSetUserSubscription,
  useSetUserToplistLeague,
  useUser,
} from '~/apis';
import { useTranslation } from '~/app';
import { useDebugEmailVerifiedOverride } from '~/app/DebugEmailVerifiedContext';
import { TextField } from '~/components/Form';
import Link from '~/components/Link';
import { Select, SelectItem } from '~/components/Select';
import { LeagueType, UserRole, UserSubscription } from '~/server/src/dtos';
import { cn } from '~/utils/styles';

interface DebugControlsProps {
  className?: string;
}

const DebugControls = ({ className }: DebugControlsProps): JSX.Element => {
  const { t } = useTranslation();
  const { data: account } = useAccount();
  const { override: debugEmailVerifiedOverride, setOverride: setDebugEmailVerifiedOverride } =
    useDebugEmailVerifiedOverride();

  const [toplistLeague, setToplistLeague] = useState(account?.toplistLeague ?? LeagueType.Bronze);
  const [subscription, setSubscription] = useState(account?.subscription ?? UserSubscription.Basic);

  const [otherUserId, setOtherUserId] = useState('');
  const [otherToplistLeague, setOtherToplistLeague] = useState(LeagueType.Bronze);
  const [otherSubscription, setOtherSubscription] = useState(UserSubscription.Basic);

  const { data: otherUser } = useUser(otherUserId, { enabled: otherUserId.trim() !== '' });

  const setUserRoleApi = useSetUserRole();
  const setUserToplistLeague = useSetUserToplistLeague();
  const setUserSubscriptionApi = useSetUserSubscription();

  const setAccountRole = (value: UserRole) => {
    if (!account) return;
    setUserRoleApi.mutate({ userId: account.userId, role: value });
  };

  const setAccountToplistLeague = (value: LeagueType) => {
    if (!account) return;
    setToplistLeague(value as LeagueType);
    setUserToplistLeague.mutate({ userId: account.userId, toplistLeague: value });
  };

  const setAccountSubscription = (value: UserSubscription) => {
    if (!account) return;
    setSubscription(value as UserSubscription);
    setUserSubscriptionApi.mutate({
      userId: account.userId,
      subscription: value,
      subscriptionValidUntil: new Date(new Date().getTime() + 1000000000000).toString(),
    });
  };

  const setOtherUserStatus = (value: LeagueType) => {
    if (!otherUser) return;
    setOtherToplistLeague(value);
    setUserToplistLeague.mutate({ userId: otherUserId, toplistLeague: value });
  };

  const setOtherUserSubscription = (value: UserSubscription) => {
    if (!otherUser) return;
    setOtherSubscription(value);
    setUserSubscriptionApi.mutate({
      userId: otherUserId,
      subscription: value,
      subscriptionValidUntil: new Date(new Date().getTime() + 1000000000000).toString(),
    });
  };

  return (
    <div
      className={cn(
        'flex flex-col space-y-6 @md:flex-row @md:justify-center @md:space-y-0 @md:space-x-8',
        className,
      )}
    >
      <div className="flex flex-col space-y-2 @md:w-80">
        <div className="flex flex-col">
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setDebugEmailVerifiedOverride(!debugEmailVerifiedOverride)}
              className="text-sm text-primary hover:underline"
            >
              {(() => {
                const verifyLabel = t('debug_verify_email_dev');
                const unverifyLabel = t('debug_unverify_email_dev');
                return debugEmailVerifiedOverride
                  ? unverifyLabel === 'debug_unverify_email_dev'
                    ? 'Unverify email (dev)'
                    : unverifyLabel
                  : verifyLabel === 'debug_verify_email_dev'
                    ? 'Verify email (dev)'
                    : verifyLabel;
              })()}
            </button>
            {debugEmailVerifiedOverride && (
              <span className="ml-2 text-xs text-yellow-600">(dev override on)</span>
            )}
          </div>
          <div className="mb-4 text-sm">
            {account?.role === UserRole.Admin ? (
              <div>
                <span>Du bist Admin.</span>{' '}
                <Link
                  underline="hover"
                  className="cursor-pointer"
                  onClick={() => setAccountRole(UserRole.User)}
                >
                  Werde wieder normal
                </Link>
              </div>
            ) : (
              <div>
                <span className="text-red-600">Du bist kein Admin.</span>{' '}
                <Link
                  underline="hover"
                  className="cursor-pointer"
                  onClick={() => setAccountRole(UserRole.Admin)}
                >
                  Werde Admin
                </Link>
              </div>
            )}
          </div>

          <Select
            value={toplistLeague}
            onValueChange={(value) => setAccountToplistLeague(value as LeagueType)}
            disabled={!account}
            label="Setze eigenen Toplist Typen"
          >
            <SelectItem value={LeagueType.Bronze}>Bronze</SelectItem>
            <SelectItem value={LeagueType.Silver}>Silber</SelectItem>
            <SelectItem value={LeagueType.Gold}>Gold</SelectItem>
          </Select>

          <div className="h-2" />

          <Select
            value={subscription}
            onValueChange={(value) => setAccountSubscription(value as UserSubscription)}
            disabled={!account}
            label="Setze Subscription Level"
          >
            <SelectItem value={UserSubscription.Basic}>Basis</SelectItem>
            <SelectItem value={UserSubscription.Premium}>Premium</SelectItem>
          </Select>
        </div>
      </div>

      <div className="flex flex-col space-y-2 @md:w-80">
        <h2 className="font-bold">Account konfigurieren</h2>
        <div className="flex flex-col">
          <TextField
            value={otherUserId}
            onChange={({ target: { value } }) => setOtherUserId(value)}
            label="UserID"
            placeholder="0de7e905-cc4d-4db7-8d4c-15976c9940b2"
          />

          <div className="h-2" />

          <Select
            value={otherToplistLeague}
            onValueChange={(value) => setOtherUserStatus(value as LeagueType)}
            disabled={otherUserId.length === 0 || otherUser?.username === undefined}
            label={
              otherUser?.username
                ? `Setze Toplist Typen von Nutzer "${otherUser.username ?? '???'}"`
                : 'Setze Toplist Typen *Bitte gültige UserID eingeben*'
            }
          >
            <SelectItem value={LeagueType.Bronze}>Bronze</SelectItem>
            <SelectItem value={LeagueType.Silver}>Silber</SelectItem>
            <SelectItem value={LeagueType.Gold}>Gold</SelectItem>
          </Select>

          <div className="h-2" />

          <Select
            value={otherSubscription}
            onValueChange={(value) => setOtherUserSubscription(value as UserSubscription)}
            disabled={otherUserId.length === 0 || otherUser?.username === undefined}
            label={
              otherUser?.username
                ? `Setze Subscription von Nutzer "${otherUser.username ?? '???'}"`
                : 'Setze Subscription *Bitte gültige UserID eingeben*'
            }
          >
            <SelectItem value={UserSubscription.Basic}>Basis</SelectItem>
            <SelectItem value={UserSubscription.Premium}>Premium</SelectItem>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default DebugControls;
